import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';

//Components
import {NotificationManager} from 'Components/Notifications';
import {SearchInput, TableView, Tooltip as SearchTooltip} from '@gpstrackit/gtc-react-controls';
import Button from "../../../../components/Button";
import {Grid, Typography} from '@mui/material';

//Hooks
//Actions
import {clearRedux, openModalBehaviorAdvanceFilter, openModalExportBehavior} from 'Redux/actions';

//Views
import {reset, searchUnitBehaviors} from "./redux/slice";
import Preferences from "Core/containers/Preferences";

//Slice
//Styles
import './styles.scss';
import {BEHAVIOR_DATE_TODAY, getBaseHeaderStyles, getGradeColor, toCamelCase} from "./redux/helpers";
import AdvanceFilterReport from "./modals/filter";
import {GetFormatForMoment} from "Core/data/Helpers";
import moment from "moment";
import Pill from "../../../../components/Pill";
import {Tooltip} from "Components";
import ExportBehaviorReport from "Modules/behavior/views/report/modals/export";
import {getBaseStyles} from "Modules/behavior/views/report/redux/helpers";

const BehaviorReportView = (props) => {
    const {messages, locale} = useIntl();
    const tableRef = useRef();
    const preferencesRef = useRef();
    const dispatch = useDispatch();

    //Local States
    const [scores, setScores] = useState([25, 50]);
    const [columns, setColumns] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [customColumns, setCustomColumns] = useState([]);
    const [customStyleHeader, setCustomStyleHeader] = useState({});
    const [customStyleColumns, setCustomStyleColumns] = useState({});
    const [debouncedText, setDebouncedText] = useState("");
    const [currentSort, setCurrentSort] = useState(null);

    //Redux States
    const user = useSelector((state) => state.securityRedux.user);
    const exportResult = useSelector(state => state.commomRedux.exportResult);
    const exportName = useSelector(state => state.commomRedux.exportName);
    const openModalExportReport = useSelector(state => state.reportExportBehaviorsRedux.open);
    const {
        open: openModalAdvanceFilterMetric,
        hasFilters,
        filter,
        limit
    } = useSelector(state => state.reportBehaviorAdvanceFilterRedux);
    const {
        loading,
        items,
        total,
        reportResult,
        error,
    } = useSelector(state => state.reportSearchUnitBehaviorsRedux);
    const searchPlaceholder = useMemo(() => {
        const field = filter?.entity === 'Driver' ? messages['behavior_driver'] : messages['behavior_vehicle']
        return String(messages['behavior_search'] || 'Search by {fields}').replace('{fields}', field || '')
    }, [messages, filter?.entity]);

    useEffect(() => {
        let objCategory = {};
        const custom = items.length ? items[0].metricTotal?.map(map => {
            objCategory[map.category] = {
                minWidth: getBaseStyles(map.category, items[0].metricTotal.length)
            }
            return map.category
        }) : [];
        setCustomStyleColumns(objCategory);
        setCustomStyleHeader(getBaseHeaderStyles(custom.length))
        setCustomColumns(custom);
        setColumns([
            {
                key: 'icon',
                sort: false,
                show: true,
                title: '',
                export: false,
                width: '5%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>
                            <Button
                                type="icon"
                                icon={filter?.entity === 'Driver' ? "person" : "directions_car"}
                                style={{fontSize: 24, color: getGradeColor(item.score, scores)}}
                                className="btn-no-padding hover-item"
                            />
                        </Grid>
                    )
                },
                exportValue: (item) => filter?.entity,
                functionString: 'return row?.icon'
            },
            {
                key: 'name',
                sort: true,
                show: true,
                title: filter?.entity === 'Driver' ? messages["behavior_driver"] : messages["behavior_vehicle"],
                export: true,
                width: '50%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>{item?.name}</Grid>
                    )
                },
                exportValue: (item) => {
                    return item?.name
                },
                sortValue: (item) => item?.name,
                functionString: 'return row?.name'
            },
            {
                key: 'aggression',
                sort: false,
                show: custom.includes('aggression'),
                title: messages["behavior_aggression"],
                export: true,
                width: '13%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>{item?.aggression || 0}</Grid>
                    )
                },
                exportValue: (item) => {
                    return item?.aggression || '0'
                },
                sortValue: (item) => item?.aggression || 0,
                functionString: 'return row?.aggression || "0"'
            },
            {
                key: 'driverMonitoringSystem',
                sort: false,
                show: custom.includes('driverMonitoringSystem'),
                title: <>
                    <Tooltip
                        title={messages["behavior_driverMonitoringSystem"]}
                    >(DMS)
                    </Tooltip>
                </>,
                export: true,
                width: '10%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>{item?.driverMonitoringSystem || 0}</Grid>
                    )
                },
                exportValue: (item) => {
                    return item.driverMonitoringSystem || '0'
                },
                sortValue: (item) => item.driverMonitoringSystem || 0,
                functionString: `return row?.driverMonitoringSystem || '0'`
            },
            {
                key: 'advancedDriverAssistanceSystem',
                sort: false,
                show: custom.includes('advancedDriverAssistanceSystem'),
                title: <>
                    <Tooltip
                        title={messages["behavior_advancedDriverAssistanceSystem"]}
                    >(ADAS)
                    </Tooltip>
                </>,
                export: true,
                width: '10%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>{item.advancedDriverAssistanceSystem || 0}</Grid>
                    )
                },
                exportValue: (item) => {
                    return item.advancedDriverAssistanceSystem || '0'
                },
                sortValue: (item) => item.advancedDriverAssistanceSystem || 0,
                functionString: `return row?.advancedDriverAssistanceSystem || '0'`
            },
            {
                key: 'vehicleSpeed',
                sort: false,
                show: custom.includes('vehicleSpeed'),
                title: messages["behavior_vehicleSpeed"],
                export: true,
                width: '13%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>{item.vehicleSpeed || 0}</Grid>
                    )
                },
                exportValue: (item) => {
                    return item.vehicleSpeed || 0
                },
                sortValue: (item) => item.vehicleSpeed || 0,
                functionString: `return row?.vehicleSpeed || '0'`
            },
            {
                key: 'speedingOverPosted',
                sort: false,
                show: custom.includes('speedingOverPosted'),
                title: messages["behavior_speedingOverPosted"],
                export: true,
                width: '13%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>{item.speedingOverPosted || 0}</Grid>
                    )
                },
                exportValue: (item) => {
                    return item.speedingOverPosted || 0
                },
                sortValue: (item) => item.speedingOverPosted || 0,
                functionString: `return row?.speedingOverPosted || '0'`
            },
            {
                key: 'totalSafetyEvents',
                sort: true,
                show: true,
                title: <>
                    <Tooltip
                        title={messages["behavior_totalSafetyEvents"]}
                    >{messages["behavior_total"]}
                    </Tooltip>
                </>,
                export: true,
                width: '13%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>{item?.safetyEvents || 0}</Grid>
                    )
                },
                exportValue: (item) => {
                    return item?.safetyEvents || '0'
                },
                sortValue: (item) => item?.safetyEvents || 0,
                functionString: `return row?.safetyEvents || '0'`
            },
            {
                key: 'scores',
                sort: true,
                show: true,
                title: messages['behavior_riskFactors'],
                export: true,
                width: '13%',
                render: (item) => {
                    return (
                        <Grid item sm={12}>
                            <Typography noWrap style={{color: getGradeColor(item.score, scores)}}>{(item.score && Number(item.score).toFixed(2) || 0)}</Typography>
                        </Grid>
                    )
                },
                exportValue: (item) => {
                    return (item.score && Number(item.score).toFixed(2) || 0)
                },
                exportColorValue: (item) => {
                    return getGradeColor(item.score, scores)
                },
                sortValue: (item) => item?.score || 0,
                functionString: `return row?.score || '0'`
            },
        ]);
    }, [locale, items]);

    useEffect(() => {
        if (exportResult && exportName === "searchUnitBehaviors") {
            NotificationManager.info(
                messages['exportInProcessBody'],
                messages['exportInProcessTitle'],
                4000,
                null,
                null,
                'info',
                null
            );
            dispatch(clearRedux("COMMON", {exportResult: null, exportName: null}));
        } if (reportResult) {

        }
    }, [exportResult, exportName, reportResult]);

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                error?.error || messages["errorMessage"],
                messages["error"],
                3000,
                null,
                null,
                "error"
            );
            dispatch(reset({error: null}));
        }
    }, [error]);

    useEffect(() => {
        if (filter) {
           const locateStart = filter?.dateRange?.start;
           const locateEnd = filter?.dateRange?.end;
            dispatch(searchUnitBehaviors({
                ...filter,
                isKph: user.isKph,
                dateRange: {
                    start: locateStart,
                    end: locateEnd
                },
                sort: currentSort,
                limit,
                offset: 0
            }))
            tableRef?.current?.setPage(1)
        }
    }, [filter]);

    useEffect(() => {
        // Create a timeout to delay updating the debouncedText state
        const handler = setTimeout(() => {
            setDebouncedText(searchText);
        }, 500); // Waits 500ms before updating the state

        // Cleanup function to clear the timeout if searchText changes before 500ms
        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    useEffect(() => {
        if (filter) {
            const locateStart = filter?.dateRange?.start;
            const locateEnd = filter?.dateRange?.end;

            // Dispatch an action to fetch filtered search results
            dispatch(searchUnitBehaviors({
                ...filter,
                isKph: user.isKph,
                dateRange: {
                    start: locateStart,
                    end: locateEnd
                },
                sort: currentSort,
                limit,
                openSearch: debouncedText,
                offset: 0
            }));
            tableRef?.current?.setPage(1)
        }
    }, [debouncedText, user.isKph]);

    const onCollapse = (row) => {
        const metrics = row?.metrics || [];

        if (!metrics.length) {
            return (
                <table className="MuiTable-root MuiTable-stickyHeader" style={{width: '100%'}} aria-labelledby="tableTitle" aria-label="enhanced table">
                    <tbody className="MuiTableBody-root">
                        <tr className="MuiTableRow-root" style={{height: '45px'}}>
                            <td className="MuiTableCell-root text-center" colSpan={7 + customColumns.length}>
                                {messages['behavior_noDataAvailable']}
                            </td>
                        </tr>
                    </tbody>
                </table>
            )
        }

        return (
            <>
                <table className="MuiTable-root MuiTable-stickyHeader" style={{width: '100%'}} aria-labelledby="tableTitle" aria-label="enhanced table">
                    <tbody className="MuiTableBody-root">

                    {metrics.map((item, index) => {
                        return (
                            <tr className="MuiTableRow-root" style={{height: '45px'}}>
                                <td className="MuiTableCell-root" style={{width: '5%', maxWidth: '50px'}}>
                                    <div></div>
                                </td>
                                <td className="MuiTableCell-root" style={{width: '5%', maxWidth: '50px'}}>
                                    <div></div>
                                </td>
                                <td className={"MuiTableCell-root columnDriverUnit" + customColumns.length} style={customStyleHeader.header}></td>
                                {
                                    customColumns.includes('aggression') &&
                                    <td className="MuiTableCell-root" style={customStyleColumns.aggression}>
                                        {toCamelCase(item?.aggression, messages)}
                                    </td>
                                }
                                {
                                    customColumns.includes('driverMonitoringSystem') &&
                                    <td className="MuiTableCell-root" style={customStyleColumns.driverMonitoringSystem}>
                                        {toCamelCase(item?.driverMonitoringSystem, messages)}
                                    </td>
                                }
                                {
                                    customColumns.includes('advancedDriverAssistanceSystem') &&
                                    <td className="MuiTableCell-root" style={customStyleColumns.advancedDriverAssistanceSystem}>
                                        {toCamelCase(item?.advancedDriverAssistanceSystem, messages)}
                                    </td>
                                }
                                {
                                    customColumns.includes('vehicleSpeed') &&
                                    <td className="MuiTableCell-root" style={customStyleColumns.vehicleSpeed}>
                                        {toCamelCase(item?.speed, messages, true, false, user.isKph)}
                                        {
                                            item?.speedIsEcu && <span
                                                className="ecuChip"
                                                style={{background: "green", borderRadius: "5px", marginLeft: "5px"}}
                                            >
                                                ECU
                                            </span>
                                        }
                                    </td>
                                }
                                {
                                    customColumns.includes('speedingOverPosted') &&
                                    <td className="MuiTableCell-root" style={customStyleColumns.speedingOverPosted}>
                                        {toCamelCase(item?.speedOver, messages, false, true, user.isKph)}
                                        {
                                            item?.speedOverIsEcu && <span
                                                className="ecuChip"
                                                style={{background: "green", borderRadius: "5px", marginLeft: "5px"}}
                                            >
                                                ECU
                                            </span>
                                        }
                                    </td>
                                }
                                <td className="MuiTableCell-root" style={customStyleHeader.footer}></td>
                            </tr>
                        )
                    })}
                    </tbody>
                </table>
            </>
        )

    };

    return (
        <Preferences
            ref={preferencesRef}
            preferencesKey="behaviorView"
            onInitialPreferences={(preferences, preferencesCompany) => {
                const hasFilters = ((preferences?.dateOption?.id && preferences?.dateOption?.id !== 'today') || (preferences?.entities?.length > 0) || (preferences?.risk != null && preferences?.risk !== 'All') || (preferences.entity != null && preferences.entity !== 'Unit'))
                if (preferencesCompany?.scores) {
                    let newScores = preferencesCompany?.scores

                    if (newScores) {
                        setScores(newScores);
                    }
                }
                if (!filter) {
                    dispatch(openModalBehaviorAdvanceFilter({
                        hasFilters: hasFilters,
                        filter: {...preferences,
                            scores: preferencesCompany?.scores,
                            entity: preferences.entity != null ? preferences.entity : 'Unit',
                            option: preferences.option != null ? preferences.option : 'all',
                            entities: preferences?.entities?.length > 0 ? preferences.entities : [],
                            dateOption: preferences?.dateOption?.id ? preferences?.dateOption : {id: BEHAVIOR_DATE_TODAY}
                        }
                    }))
                }
            }}
        >
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{display: 'flex'}}>
                            <Button
                                type="icon"
                                id="buttonFilterBehavior"
                                icon="filter_list"
                                tooltip={messages['table_filter']}
                                className={hasFilters ? 'select-view-icon-filter' : 'select-view-icon'}
                                disabled={loading}
                                onClick={() => dispatch(openModalBehaviorAdvanceFilter({open: !openModalAdvanceFilterMetric}))}
                            />
                            <Button
                                type="icon"
                                id="buttonFilterBehavior"
                                icon="get_app"
                                tooltip={messages['table_export']}
                                className={loading ? `color-disabled-toolbar` : `icon-down-download`}
                                disabled={loading}
                                onClick={() => {
                                    setSearchText('');
                                    dispatch(openModalBehaviorAdvanceFilter({open: false}))
                                    dispatch(openModalExportBehavior({open: true}))
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <Pill
                        textField={'name'}
                        item={{
                            id: 1,
                            name: `${messages['behavior_'+ filter?.dateOption?.id]} (${moment(filter?.dateRange?.start || moment().startOf('day').toDate()).format(GetFormatForMoment(user.dateformat))} - ${moment(filter?.dateRange?.end || moment.max(moment()).toDate()).format(GetFormatForMoment(user.dateformat))})`
                        }}
                        outlined={false}
                        colorField="default"
                        colorSelection="005bf8"
                        disabled={loading}
                        style={{border: '0', padding: '0px 3px 0 3px'}}
                        onClick={() => {
                            dispatch(openModalBehaviorAdvanceFilter({open: true}))
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchTooltip title={searchPlaceholder} placement="bottom" arrow>
                        <SearchInput
                            placeholder={searchPlaceholder}
                            searchText={searchText}
                            isDisabled={loading}
                            onSearchText={(text) => {
                                setSearchText(text);
                            }}
                        />
                    </SearchTooltip>
                </Grid>
            </Grid>
            <TableView
                ref={tableRef}
                keyColumn="name"
                data={items ? items.map(service => { return {...service, children: 1} }) : []}
                total={total}
                loading={loading}
                messageNoData={messages['behavior_tableNoData']}
                hideAllInPagination
                columns={columns}
                topheight={230}
                hideDragable
                disableItemSelection
                onCollapse={onCollapse}
                onChangePage={(newPage) => {
                    let newOffset = newPage - 1;
                    const locateStart = filter?.dateRange?.start;
                    const locateEnd = filter?.dateRange?.end;
                    dispatch(searchUnitBehaviors({
                        ...filter,
                        isKph: user.isKph,
                        dateRange: {
                            start: locateStart,
                            end: locateEnd
                        },
                        sort: currentSort,
                        openSearch: debouncedText,
                        limit,
                        offset: limit * newOffset
                    }))
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                    dispatch(openModalBehaviorAdvanceFilter({
                        limit: newRowsPerPage
                    }))
                    const locateStart = filter?.dateRange?.start;
                    const locateEnd = filter?.dateRange?.end;
                    dispatch(searchUnitBehaviors({
                        ...filter,
                        isKph: user.isKph,
                        dateRange: {
                            start: locateStart,
                            end: locateEnd
                        },
                        sort: currentSort,
                        openSearch: debouncedText,
                        limit: newRowsPerPage,
                        offset: 0
                    }))
                }}
                onChangeSort={(sort) => {
                    const locateStart = filter?.dateRange?.start;
                    const locateEnd = filter?.dateRange?.end;
                    setCurrentSort(sort);
                    dispatch(searchUnitBehaviors({
                        ...filter,
                        isKph: user.isKph,
                        dateRange: {
                            start: locateStart,
                            end: locateEnd
                        },
                        sort,
                        openSearch: debouncedText,
                        limit,
                        offset: 0
                    }))
                    tableRef?.current?.setPage(1)
                }}
            />

            {openModalExportReport && <ExportBehaviorReport preferencesRef={preferencesRef} customcolumns={customColumns} sort={currentSort}/>}
            {!openModalExportReport && <AdvanceFilterReport preferencesRef={preferencesRef}/>}
        </Preferences>
    );
};

export default BehaviorReportView;
